import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardTempCardInfo,
    GrowthDashboardReq,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthTempInfoCardProps {
    gfIdx: number;
}

const GrowthTempInfoCard = ({ gfIdx }: GrowthTempInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [tempInfo, setTempInfo] =
        useState<GrowthDashboardTempCardInfo | null>(null);

    // 일일 유효온도 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchTempInfo(gfIdx);
        }
    }, [gfIdx]);

    const fetchTempInfo = async (gfIdx: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardReq = { gfIdx };
            const response = await api.getTempCardInfo(req);
            setTempInfo(response.data);
        } catch (error) {
            console.error('일일 유효온도 정보 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (!chartRef.current || !tempInfo || tempInfo.list.length === 0)
            return;

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // 일일 유효온도 값 배열 추출
        const tempValues = tempInfo.list.map(item => item.dailyEffectiveTemp);

        const options = {
            series: [
                {
                    name: '일일 유효온도',
                    data: tempValues,
                },
            ],
            chart: {
                type: 'area',
                height: 70,
                sparkline: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 0,
                    left: 0,
                    blur: 3,
                    color: 'rgba(var(--primary-rgb))',
                    opacity: 0.4,
                },
            },
            stroke: {
                curve: 'smooth', // 곡선
                width: 1,
            },
            fill: {
                type: 'gradient',
                gradient: {
                    opacityFrom: 0.5,
                    opacityTo: 0.2,
                    stops: [0, 60],
                    colorStops: [
                        [
                            {
                                offset: 0,
                                color: 'rgba(var(--primary-rgb))',
                                opacity: 0.3,
                            },
                            {
                                offset: 60,
                                color: 'rgba(var(--primary-rgb))',
                                opacity: 0.2,
                            },
                        ],
                    ],
                },
            },
            colors: ['rgba(var(--primary-rgb))'],
            tooltip: {
                fixed: {
                    enabled: false,
                },
                x: {
                    show: false,
                },
                y: {
                    title: {
                        formatter: function () {
                            return '';
                        },
                    },
                    formatter: function (value: number) {
                        return value.toFixed(1) + '°C';
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [tempInfo]);

    // 차이 표시 계산 (증가/감소)
    const getDifferenceDisplay = () => {
        if (!tempInfo || tempInfo.tempDifference === null) return null;

        const diff = tempInfo.tempDifference;
        const isPositive = diff >= 0;
        const icon = isPositive
            ? 'ri-arrow-left-up-line'
            : 'ri-arrow-left-down-line';

        return (
            <span className="badge bg-primary rounded-pill align-items-center fs-11">
                <i className={`${icon} fs-11`}></i> {Math.abs(diff).toFixed(1)}
                °C
            </span>
        );
    };

    // 온도 상태 평가
    const getTempStatus = () => {
        if (!tempInfo) return { text: '-', color: 'secondary' };

        const temp = tempInfo.recentAvgDailyTemp;
        if (temp <= 0) return { text: '생육정지', color: 'danger' };
        if (temp < 10) return { text: '생육느림', color: 'warning' };
        if (temp >= 10 && temp <= 20) return { text: '적정', color: 'success' };
        if (temp > 20 && temp <= 25)
            return { text: '고온주의', color: 'warning' };
        return { text: '과고온', color: 'danger' };
    };

    const tempStatus = getTempStatus();

    if (loading) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="d-flex align-items-center justify-content-center">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!tempInfo) {
        return (
            <div className="card custom-card overflow-hidden">
                <div className="card-body">
                    <div className="text-center text-muted">
                        일일 유효온도 데이터를 불러올 수 없습니다
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card custom-card overflow-hidden">
            <div className="">
                <div className="card-body">
                    <div className="d-flex align-items-center w-100 justify-content-between gap-1">
                        <div>
                            <p className="mb-1 text-muted fw-medium">
                                평균 일일 유효온도
                                <Tooltip
                                    content="일일 유효온도는 작물 생육에 실제로 기여하는 하루 동안의 온도입니다. 10~20°C가 적정 범위이며, 15~20°C에서 가장 잘 자랍니다."
                                    customClass="tooltip-dark"
                                    placement="top"
                                >
                                    <i className="ri-question-line" />
                                </Tooltip>
                            </p>
                            <h4 className="mb-0 fw-medium">
                                {tempInfo.recentAvgDailyTemp
                                    ? tempInfo.recentAvgDailyTemp.toFixed(1)
                                    : '0.0'}
                                <small className="fs-11 me-2">°C</small>
                                <span
                                    className={`badge bg-${tempStatus.color}-transparent mt-1 fs-10`}
                                >
                                    {tempStatus.text}
                                </span>
                            </h4>
                        </div>
                        <div className="ms-auto text-end">
                            {getDifferenceDisplay()}
                            <div className="text-muted fs-12 mt-1">
                                2주전 대비
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    ref={chartRef}
                    id="temp-chart"
                    style={{
                        marginTop: -20,
                    }}
                ></div>
            </div>
        </div>
    );
};

export default GrowthTempInfoCard;
